import { GameConfig } from "@/config/GameConfig";
import type { ChaosOutcomeKind } from "./types";
import type { RandomSource } from "./rng";

/**
 * The CHAOS outcome roll, extracted from `LevelGame` so Daily Challenge's
 * generator/validator can deterministically simulate the exact same
 * sequence of outcomes a `chaos_only` challenge will produce for a given
 * seed (see `src/daily/validator.ts`'s achievability check), without
 * duplicating - and risking drifting from - the real cumulative-weight
 * logic. `LevelGame.resolveChaosOutcome()` is a thin wrapper around this.
 *
 * PR13-A (versioned CHAOS V2 outcome pool): `rulesVersion` selects one of
 * two frozen band tables below - `"current"` (Campaign, Daily Generator
 * V5) or `"legacy-v1"` (Daily Generator V1-V4, forever). Deliberately no
 * default value: every call site must say which pool it means, so a live
 * config change can never silently reinterpret an already-generated
 * Daily seed under the wrong weights. One shared band-walking function
 * (`resolveFromBands`) backs both tables - the resolver logic itself is
 * never duplicated per version, only the weight data is.
 */
export type ChaosRulesVersion = "current" | "legacy-v1";

interface ChaosBand {
  readonly outcome: ChaosOutcomeKind;
  readonly weight: number;
}

/**
 * PR13-A: the current, active pool (Campaign / Daily Generator V5).
 * Evaluation order: JACKPOT -> WORMHOLE -> SHRINK -> SPEED TRIAL - the
 * last entry is the implicit remainder (no explicit upper-bound check),
 * so it also silently absorbs any float-rounding slack. SCORE HIT is
 * never in this table - it is structurally unreachable under current
 * rules, not merely weighted to 0.
 */
const CURRENT_V2_BANDS: readonly ChaosBand[] = [
  { outcome: "jackpot", weight: GameConfig.bites.chaos.outcomeWeightsCurrentV2.jackpot },
  { outcome: "wormhole", weight: GameConfig.bites.chaos.outcomeWeightsCurrentV2.wormhole },
  { outcome: "shrink", weight: GameConfig.bites.chaos.outcomeWeightsCurrentV2.shrink },
  { outcome: "speed-trial", weight: GameConfig.bites.chaos.outcomeWeightsCurrentV2.speedTrial },
];

/**
 * FROZEN legacy pool (pre-PR13-A) - Daily Generator V1-V4 use this
 * exactly forever. Evaluation order: JACKPOT -> WORMHOLE -> SCORE HIT ->
 * SHRINK -> SPEED TRIAL (unchanged from before PR13-A) - the last entry
 * is again the implicit remainder.
 */
const LEGACY_V1_BANDS: readonly ChaosBand[] = [
  { outcome: "jackpot", weight: GameConfig.bites.chaos.outcomeWeightsLegacyV1.jackpot },
  { outcome: "wormhole", weight: GameConfig.bites.chaos.outcomeWeightsLegacyV1.wormhole },
  { outcome: "score-hit", weight: GameConfig.bites.chaos.outcomeWeightsLegacyV1.scoreHit },
  { outcome: "shrink", weight: GameConfig.bites.chaos.outcomeWeightsLegacyV1.shrink },
  { outcome: "speed-trial", weight: GameConfig.bites.chaos.outcomeWeightsLegacyV1.speedTrial },
];

/**
 * Boundaries are computed from rounded whole-percent thresholds, not by
 * accumulating the raw fractional weights - plain repeated float addition
 * (0.25 + 0.2 + 0.2 + 0.2) lands on 0.8500000000000001, not 0.85, which
 * would silently shift a boundary by a floating-point rounding artifact
 * instead of the documented value. The final band in `bands` is always
 * the remainder - never given an explicit upper-bound check - so it also
 * absorbs any residual rounding slack, exactly as before PR13-A.
 */
function resolveFromBands(chaosRandom: RandomSource, bands: readonly ChaosBand[]): ChaosOutcomeKind {
  const roll100 = chaosRandom() * 100;
  const pct = (n: number) => Math.round(n * 100);
  let cumulative = 0;
  for (let i = 0; i < bands.length - 1; i++) {
    cumulative += pct(bands[i].weight);
    if (roll100 < cumulative) return bands[i].outcome;
  }
  return bands[bands.length - 1].outcome;
}

export function resolveChaosOutcome(chaosRandom: RandomSource, rulesVersion: ChaosRulesVersion): ChaosOutcomeKind {
  return resolveFromBands(chaosRandom, rulesVersion === "legacy-v1" ? LEGACY_V1_BANDS : CURRENT_V2_BANDS);
}
